"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import type { Id } from "@convex/_generated/dataModel";
import { Home, Settings } from "@/components/icons";
import type { NavItem } from "@/components/app-sidebar/types";
import { SETTINGS_PARAM, settingsHref } from "@shared/routing/settings-path";
import { isRouteActive } from "@/lib/navigation/active-state";
import { cn } from "@/lib/utils";
import type { DashboardAudience } from "./navigation/account-type-policy";
import {
  isBuyerAccount,
  isFreightCarrierAccount,
  isVendorAccount,
} from "./navigation/account-type-policy";
import {
  BROWSE_ITEM,
  BUYER_REQUESTS_ITEM,
  CARRIER_BIDS_ITEM,
  HELP_ITEM,
  LOADS_ITEM,
  MY_LISTINGS_ITEM,
  ORDERS_ITEM,
  QUOTES_ITEM,
} from "./navigation";
import { useNavBadgeCounts } from "./use-nav-badge-counts";

interface MobileTab {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  activePaths?: string[];
  exact?: boolean;
  opensSettings?: boolean;
}

const MOBILE_TAB_SLOTS = 4;

function navTab(item: NavItem): MobileTab {
  return {
    label: item.shortLabel ?? item.label,
    href: item.href,
    icon: item.icon,
    activePaths: item.activePaths,
    exact: item.exact,
  };
}

const HOME_TAB: MobileTab = { label: "Home", href: "/dashboard", icon: Home };
const ORDERS_TAB = navTab(ORDERS_ITEM);
const BROWSE_TAB = navTab(BROWSE_ITEM);
const LISTINGS_TAB = navTab(MY_LISTINGS_ITEM);
const LOADS_TAB = navTab(LOADS_ITEM);
const HELP_TAB = navTab(HELP_ITEM);

const buyerTabs: MobileTab[] = [HOME_TAB, BROWSE_TAB, navTab(QUOTES_ITEM), ORDERS_TAB];

const sellerTabs: MobileTab[] = [
  HOME_TAB,
  LISTINGS_TAB,
  navTab(BUYER_REQUESTS_ITEM),
  ORDERS_TAB,
];

const hybridTabs: MobileTab[] = [HOME_TAB, BROWSE_TAB, LISTINGS_TAB, ORDERS_TAB];

const freightCarrierTabs: MobileTab[] = [
  HOME_TAB,
  LOADS_TAB,
  navTab(CARRIER_BIDS_ITEM),
  HELP_TAB,
];

const noMarketplaceTabs: MobileTab[] = [
  HOME_TAB,
  {
    label: "Settings",
    href: settingsHref("profile"),
    icon: Settings,
    opensSettings: true,
  },
  HELP_TAB,
];

function marketplaceTabsFor(audience: DashboardAudience): MobileTab[] {
  const isBuyer = isBuyerAccount(audience);
  const isVendor = isVendorAccount(audience);
  if (isBuyer && isVendor) return hybridTabs;
  if (isVendor) return sellerTabs;
  if (isBuyer) return buyerTabs;
  return noMarketplaceTabs;
}

function isTabActive(tab: MobileTab, pathname: string, settingsOpen: boolean) {
  if (tab.opensSettings || settingsOpen) return Boolean(tab.opensSettings) && settingsOpen;
  return (
    isRouteActive(pathname, tab.href, { exact: tab.exact }) ||
    (tab.activePaths ?? []).some((href) => isRouteActive(pathname, href))
  );
}

export function tabsForAccountType(audience: DashboardAudience): MobileTab[] {
  if (audience.isFreightOnly) return freightCarrierTabs;
  const marketplaceTabs = marketplaceTabsFor(audience);
  if (!isFreightCarrierAccount(audience)) return marketplaceTabs;
  return [
    ...marketplaceTabs.filter((tab) => tab !== ORDERS_TAB).slice(0, MOBILE_TAB_SLOTS - 2),
    ORDERS_TAB,
    LOADS_TAB,
  ];
}

const BADGE_CLASS =
  "absolute -right-2.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-micro font-semibold leading-none text-destructive-foreground";

function MobileNavTab({
  tab,
  active,
  badgeCount,
}: {
  tab: MobileTab;
  active: boolean;
  badgeCount: number;
}) {
  const Icon = tab.icon;
  return (
    <Link
      href={tab.href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "relative flex min-h-12 flex-1 flex-col items-center justify-center gap-0.5 pt-1.5 pb-1 text-micro font-medium transition-colors",
        active ? "text-action" : "text-muted-foreground",
      )}
    >
      <span className="relative flex items-center justify-center">
        <Icon className="size-6 shrink-0" />
        {badgeCount > 0 ? (
          <span className={BADGE_CLASS}>
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        ) : null}
      </span>
      {tab.label}
    </Link>
  );
}

export function DashboardMobileNav({
  companyId,
  audience,
}: {
  companyId?: Id<"companies">;
  audience: DashboardAudience;
}) {
  const pathname = usePathname();
  const settingsOpen = useSearchParams().has(SETTINGS_PARAM);
  const badgeCounts = useNavBadgeCounts(companyId, audience);
  const tabs = tabsForAccountType(audience);
  return (
    <nav
      aria-label="Dashboard"
      className="flex shrink-0 border-t border-border bg-background pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      {tabs.map((tab) => (
        <MobileNavTab
          key={tab.href}
          tab={tab}
          active={isTabActive(tab, pathname, settingsOpen)}
          badgeCount={badgeCounts[tab.href] ?? 0}
        />
      ))}
    </nav>
  );
}
